import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { listTenants } from '../../api/tenants';
import { listSuburbs, createSuburb, updateSuburb, deleteSuburb } from '../../api/suburbs';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Pagination,
  Stack,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useMobileLayout } from '../../utils/useMobileLayout';
import ResponsiveList from '../../components/common/ResponsiveList';

const SuburbList = () => {
  const navigate = useNavigate();
  const { isMobile } = useMobileLayout();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suburbs, setSuburbs] = useState([]);
  const [totalSuburbs, setTotalSuburbs] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedSuburb, setSelectedSuburb] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [newSuburbName, setNewSuburbName] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editSuburbName, setEditSuburbName] = useState('');
  const [editSuburbTenant, setEditSuburbTenant] = useState('');

  // Fetch tenants
  const fetchTenants = async () => {
    try {
      const data = await listTenants({ page: 1, page_size: 100 });
      setTenants(data.tenants);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      setError('Failed to load tenants. Please try again later.');
    }
  };

  // Fetch suburbs
  const fetchSuburbs = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = { page, page_size: pageSize };
      if (search) {
        params.search = search;
      }
      if (selectedTenant) {
        params.tenant_id = selectedTenant;
      }

      const data = await listSuburbs(params);
      setSuburbs(data.suburbs);
      setTotalSuburbs(data.total);
      setTotalPages(data.total_pages);
    } catch (error) {
      console.error('Error fetching suburbs:', error);
      setError('Failed to load suburbs. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    fetchSuburbs();
  }, [page, search, selectedTenant]);

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Handle search
  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1); // Reset to first page when search changes
  };

  // Clear search
  const handleClearSearch = () => {
    setSearch('');
    setPage(1);
  };

  // Handle tenant filter change
  const handleTenantChange = (event) => {
    setSelectedTenant(event.target.value);
    setPage(1); // Reset to first page when tenant filter changes
  };

  // Open add suburb dialog
  const handleOpenAddDialog = () => {
    setNewSuburbName('');
    setIsAddDialogOpen(true);
  };

  // Close add suburb dialog
  const handleCloseAddDialog = () => {
    setIsAddDialogOpen(false);
  };

  // Handle add suburb
  const handleAddSuburb = async () => {
    if (!newSuburbName || !selectedTenant) {
      setError('Please enter a suburb name and select a tenant.');
      return;
    }

    setIsLoading(true);

    try {
      await createSuburb({
        name: newSuburbName,
        tenant_id: parseInt(selectedTenant)
      });
      
      // Refresh suburb list
      fetchSuburbs();
      setIsAddDialogOpen(false);
    } catch (error) {
      console.error('Error adding suburb:', error);
      setError('Failed to add suburb. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit suburb
  const handleEditSuburb = (suburb) => {
    setSelectedSuburb(suburb);
    setEditSuburbName(suburb.name);
    setEditSuburbTenant(suburb.tenant_id.toString());
    setIsEditDialogOpen(true);
  };

  // Close edit suburb dialog
  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
  };

  // Handle update suburb
  const handleUpdateSuburb = async () => {
    if (!editSuburbName || !editSuburbTenant) {
      setError('Please enter a suburb name and select a tenant.');
      return;
    }

    setIsLoading(true);

    try {
      await updateSuburb(selectedSuburb.id, {
        name: editSuburbName,
        tenant_id: parseInt(editSuburbTenant)
      });
      
      // Refresh suburb list
      fetchSuburbs();
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating suburb:', error);
      setError('Failed to update suburb. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete suburb
  const handleDeleteSuburb = (suburb) => {
    setSelectedSuburb(suburb);
    setOpenDialog(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    setIsLoading(true);

    try {
      await deleteSuburb(selectedSuburb.id);
      // Refresh suburb list
      fetchSuburbs();
    } catch (error) {
      console.error('Error deleting suburb:', error);
      setError('Failed to delete suburb. Please try again later.');
    } finally {
      setIsLoading(false);
      setOpenDialog(false);
    }
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Get tenant name by ID
  const getTenantName = (tenantId) => {
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant ? tenant.name : 'Unknown';
  };

  if (isLoading && suburbs.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Suburb Management
        </Typography>

        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              label="Search Suburbs"
              variant="outlined"
              size="small"
              value={search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="tenant-filter-label">Filter by Tenant</InputLabel>
              <Select
                labelId="tenant-filter-label"
                id="tenant-filter"
                value={selectedTenant}
                label="Filter by Tenant"
                onChange={handleTenantChange}
              >
                <MenuItem value="">All Tenants</MenuItem>
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={tenant.id.toString()}>
                    {tenant.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
          >
            Add Suburb
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <ResponsiveList
          items={suburbs}
          empty="No suburbs found."
          getKey={(s) => s.id}
          columns={[
            { key: 'name', header: 'Name', render: (s) => s.name },
            { key: 'tenant', header: 'Tenant', render: (s) => getTenantName(s.tenant_id) },
            { key: 'created', header: 'Created', render: (s) => new Date(s.created_at).toLocaleDateString() },
            {
              key: 'actions',
              header: 'Actions',
              render: (s) => (
                <>
                  <Tooltip title="Edit">
                    <IconButton size="small" color="primary" onClick={() => handleEditSuburb(s)} sx={{ mr: 1 }}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => handleDeleteSuburb(s)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </>
              ),
            },
          ]}
          mobileCard={(s) => (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{s.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{getTenantName(s.tenant_id)}</Typography>
                </Box>
                <Box>
                  <IconButton size="small" color="primary" onClick={() => handleEditSuburb(s)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDeleteSuburb(s)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Added {new Date(s.created_at).toLocaleDateString()}
              </Typography>
            </Box>
          )}
        />

        {suburbs.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Stack spacing={2}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
              <Typography variant="body2" color="text.secondary" align="center">
                Showing {suburbs.length} of {totalSuburbs} suburbs
              </Typography>
            </Stack>
          </Box>
        )}
      </Box>

      {/* Add Suburb Dialog */}
      <Dialog open={isAddDialogOpen} onClose={handleCloseAddDialog} fullScreen={isMobile} fullWidth maxWidth="sm">
        <DialogTitle>Add New Suburb</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, width: '400px', maxWidth: '100%' }}>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label="Suburb Name"
              type="text"
              fullWidth
              variant="outlined"
              value={newSuburbName}
              onChange={(e) => setNewSuburbName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth>
              <InputLabel id="add-tenant-label">Tenant</InputLabel>
              <Select
                labelId="add-tenant-label"
                id="add-tenant"
                value={selectedTenant}
                label="Tenant"
                onChange={handleTenantChange}
              >
                <MenuItem value="">Select a tenant</MenuItem>
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={tenant.id.toString()}>
                    {tenant.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Cancel</Button>
          <Button onClick={handleAddSuburb} variant="contained" color="primary" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Suburb Dialog */}
      <Dialog open={isEditDialogOpen} onClose={handleCloseEditDialog} fullScreen={isMobile} fullWidth maxWidth="sm">
        <DialogTitle>Edit Suburb</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, width: '400px', maxWidth: '100%' }}>
            <TextField
              autoFocus
              margin="dense"
              id="edit-name"
              label="Suburb Name"
              type="text"
              fullWidth
              variant="outlined"
              value={editSuburbName}
              onChange={(e) => setEditSuburbName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth>
              <InputLabel id="edit-tenant-label">Tenant</InputLabel>
              <Select
                labelId="edit-tenant-label"
                id="edit-tenant"
                value={editSuburbTenant}
                label="Tenant"
                onChange={(e) => setEditSuburbTenant(e.target.value)}
              >
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={tenant.id.toString()}>
                    {tenant.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button onClick={handleUpdateSuburb} variant="contained" color="primary" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
      >
        <DialogTitle>Delete Suburb</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete suburb "{selectedSuburb?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" disabled={isLoading}>
            {isLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SuburbList;